import React, { useEffect, useState } from "react";
import Checkbox from "./Checkbox";
import "./contactForm.css";
import RadioButton from "./RadioButton";

export enum EContactFormLayout {
  centered = "centered",
  column2 = "column2",
  column3 = "column3",
  column2static = "column2static",
}

export enum EInfoPosition {
  before = "before",
  after = "after",
}

export interface ICheckBoxGroups {
  label?: string;
  groups?: {
    legend?: string;
    list: string[];
  }[];
  nestedGroups?: {
    legend?: string;
    list: {
      value: string;
      sublist?: string[];
    }[];
  }[];
}

export interface IRadioButtonGroup {
  label?: string;
  list: string[];
}

export interface IContactFormProps {
  classNames?: string;
  children?: React.ReactNode;
  infoPosition?: EInfoPosition;
  clickHandler?: (formValue?: any) => any;
  backgroundImage?: string;
  title?: string;
  mainLegendClassName?: string;
  fieldsetClassName?: string;
  mainListClassName?: string;
  inputTextClassNames?: string;
  inputSelect?: { label: string; list: string[] };
  inputSelectLabelAsOption?: boolean;
  inputSelectClassNames?: string;
  selectWrapperClassNames?: string;
  checkboxGroups?: ICheckBoxGroups;
  checkboxGroupsWrapperClassNames?: string;
  checkboxListClassName?: string;
  radiobtnGroup?: IRadioButtonGroup;
  radiobtnGroupLabelClassNames?: string;
  radiobtnGroupWrapperClassNames?: string;
  radiobtnListClassName?: string;
  inputTextAreaLabel?: string;
  inputTextAreaRequired?: boolean;
  inputTextAreaClassName?: string;
  addExtraTextInput?: boolean;
  extraTextInputPlaceholder?: string;
  layout?: EContactFormLayout;
  invertedForm?: boolean;
  submitButtonClassName?: string;
  formId?: string;
  firstNamePlaceholder?: string;
  emailPlaceholder?: string;
  phonePlaceholder?: string;
  lastNamePlaceholder?: string;
  inputRange?: {
    label: string;
  }
  inputTextRangeClassNames?: string;
  unwantedTextInputs?: ("firstName" | "lastName" | "phoneNumber" | "email")[];
  hideReCaptchaText?: boolean;
  optInText?: string;
  optInTextClassName?: string;
  showForm?: boolean;
}

export const ContactForm = ({
  classNames,
  children,
  infoPosition = EInfoPosition.before,
  clickHandler,
  backgroundImage,
  title = "Contact",
  inputTextClassNames,
  mainLegendClassName = "",
  fieldsetClassName = "",
  mainListClassName = "",
  submitButtonClassName = "",
  inputSelect,
  inputSelectLabelAsOption = true,
  inputSelectClassNames = "",
  selectWrapperClassNames = "",
  checkboxGroups,
  checkboxGroupsWrapperClassNames = "",
  checkboxListClassName = "",
  radiobtnGroup,
  radiobtnGroupLabelClassNames = "",
  radiobtnGroupWrapperClassNames = "",
  radiobtnListClassName = "",
  addExtraTextInput = false,
  extraTextInputPlaceholder = "",
  inputTextAreaLabel,
  inputTextAreaRequired = false,
  layout = EContactFormLayout.centered,
  inputTextAreaClassName = "",
  formId,
  inputRange,
  firstNamePlaceholder,
  lastNamePlaceholder,
  emailPlaceholder,
  phonePlaceholder,
  inputTextRangeClassNames,
  unwantedTextInputs = [],
  hideReCaptchaText = false,
  optInText,
  optInTextClassName = "",
  showForm = true
}: IContactFormProps) => {

  if(!showForm) {
    return <></>
  }

  // @TODO: Submission handling/recaptcha
  const bgImageUrl = backgroundImage ? `url('${backgroundImage}')` : "";
  const inputNotCheckboxBase = `border-body-text placeholder-body-text leading-10 w-full rounded-none`;

  const [isSendingRequest, setisSendingRequest] = useState(false)
  const [isRequestCompleted, setisRequestCompleted] = useState(false)

  const checboxChangeHandler = (target: any) => {
    const boxesList = document.getElementById(`list_${target.id}`)
    if (target.checked) {
      boxesList && (boxesList.style.display = "grid")
    }
    else {
      boxesList && (boxesList.style.display = "none")
      boxesList?.querySelectorAll('input[type=checkbox]')?.forEach((item: any) => {
        item.checked = false
      })
    }
  }

  let layoutVariant = {
    mainFieldset: "",
    mainLegend: "",
    mainList: "",
    textCentering: "",
    inputNotCheckbox: "",
    selectWrapper: "",
  };

  switch (layout) {
    case EContactFormLayout.column2:
      layoutVariant = {
        ...layoutVariant,
        mainFieldset: "border-t flex flex-wrap justify-around",
        mainLegend:
          "py-8 px-4 text-left italic sm:ml-8 sm:px-8 py-20 uppercase",
        mainList: "md:mt-12 md:mx-0 md:mb-8",
        inputNotCheckbox: `${inputNotCheckboxBase} border px-3`,
        selectWrapper: "border px-3",
      };

      break;
    case EContactFormLayout.centered:
      layoutVariant = {
        ...layoutVariant,
        mainLegend: "text-center pt-10",
        mainList: "md:my-12 md:mx-auto",
        textCentering: "text-center",
        inputNotCheckbox: `${inputNotCheckboxBase} border-b`,
        selectWrapper: "border-b",
      };
      break;
    case EContactFormLayout.column3:
      layoutVariant = {
        ...layoutVariant,
        mainLegend: "text-left pt-10 uppercase",
        mainList: "md:w-full md:max-w-full md:my-12 md:mx-0",
        textCentering: "text-center",
        inputNotCheckbox: `${inputNotCheckboxBase} border-b`,
        selectWrapper: "border-b",
      };
      break;
    case EContactFormLayout.column2static:
      layoutVariant = {
        ...layoutVariant,
        mainFieldset:
          "border-0 flex flex-wrap md:flex-nowrap justify-between inverted max-w-6xl mx-auto",
        mainLegend: "py-8 text-left md:pt-20 md:text-5xl",
        mainList: "md:mt-12 md:mx-0 md:mb-8",
        inputNotCheckbox: `${inputNotCheckboxBase} border px-3`,
      };
      break;
  }

  return isRequestCompleted ? (
    <fieldset
      className={`${fieldsetClassName} px-5 pb-5 lg:px-10 lg:pb-10 border-divider ${layoutVariant.mainFieldset}`}
    >
      <legend className={`${mainLegendClassName} text-xl font-bold ${layoutVariant.mainLegend}`}>
        Your Information Was Sent To Us...
      </legend>
    </fieldset>
  ) : (
    <form
      id={formId}
      className={`contact-form  bg-no-repeat bg-cover ${layout} ${classNames}`}
      style={{ backgroundImage: bgImageUrl }}
      onSubmit={async (e: React.SyntheticEvent) => {
        e.preventDefault();

        const target: any = e.target;

        let formValues: any = {
          firstName: target.firstname?.value,
          lastName: target.lastname?.value,
          email: target.email?.value,
          phone: target.phone?.value,
        };

        if (inputSelect) {
          formValues.inputSelect = {
            label: inputSelect.label,
            value: target["Select List"].value,
          };
        }

        if (inputTextAreaLabel) {
          formValues.message = target.textarea.value;
        }

        if (checkboxGroups) {
          formValues.interestedIn = [];
          checkboxGroups.groups?.forEach((group, i) => {
            group.list.forEach((label, j) => {
              if ((target[`box_${i}_${j}`] as HTMLInputElement).checked) {
                formValues.interestedIn.push(label);
              }
            });
          });

          checkboxGroups.nestedGroups?.forEach((group, i) => {
            group.list.forEach((item, j) => {
              let interestItem: any[] = [];
              if ((target[`box_${i}_${j}`] as HTMLInputElement).checked) {
                interestItem.push(item.value);
                item.sublist?.forEach((label, k) => {
                  if ((target[`box_${i}_${j}_${k}`] as HTMLInputElement).checked) {
                    interestItem.push(label);
                  }
                });
                if (interestItem.length > 0) {
                  formValues.interestedIn.push(interestItem.join(" - "));
                }
              }
            });
          });
        }

        if (inputRange) {
          formValues.priceRange = {
            min: target["rangeMin"].value,
            max: target["rangeMax"].value,
          };
        }

        if (radiobtnGroup && target["radioOption"]?.value) {
          formValues.radioOption = {
            label: radiobtnGroup.label,
            value: target["radioOption"].value,
          }
        }

        if (addExtraTextInput && target.extraInput?.value) {
          formValues.extraInput = {
            label: extraTextInputPlaceholder,
            value: target.extraInput?.value,
          }
        }

        setisSendingRequest(true)

        clickHandler && await clickHandler(formValues);

        setisSendingRequest(false)
        setisRequestCompleted(true)
      }}
    >
      <fieldset
        className={`${fieldsetClassName} px-5 pb-5 lg:px-10 lg:pb-10 border-divider ${layoutVariant.mainFieldset}`}
      >
        <legend className={`${mainLegendClassName} text-2xl font-bold ${layoutVariant.mainLegend}`}>
          {title}
        </legend>
        {children && infoPosition == EInfoPosition.before && <>{children}</>}

        <ul
          className={`main-list ${mainListClassName} list-none mt-12 mb-12 mx-auto max-w-7xl grid gap-4 ${layoutVariant.mainList}`}
        >
          {!unwantedTextInputs.includes("firstName") && <li style={{ gridArea: "firstname" }} key="first">
            <label htmlFor="firstname" className="sr-only">
              First name
            </label>
            <input
              type="text"
              name="firstname"
              id="firstname"
              placeholder={firstNamePlaceholder || "FIRST NAME"}
              required
              className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextClassNames}`}
            />
          </li>}
          {!unwantedTextInputs.includes("lastName") && <li style={{ gridArea: "lastname" }} key="last">
            <label htmlFor="lastname" className="sr-only">
              Last name
            </label>
            <input
              type="text"
              name="lasttname"
              id="lastname"
              placeholder={lastNamePlaceholder || "LAST NAME"}
              required
              className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextClassNames}`}
            />
          </li>}
          {!unwantedTextInputs.includes("email") && <li style={{ gridArea: "email" }} key="email">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              id="email"
              placeholder={emailPlaceholder || "EMAIL ADDRESS"}
              required
              className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextClassNames}`}
            />
          </li>}
          {!unwantedTextInputs.includes("phoneNumber") && <li style={{ gridArea: "phone" }} key="phone">
            <label htmlFor="phone" className="sr-only">
              Phone number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              pattern="^([0-9]|\+|\(|\))*(\.|[0-9]| |-)*$"
              required
              placeholder={phonePlaceholder || "PHONE NUMBER"}
              className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextClassNames}`}
            ></input>
          </li>}
          {inputSelect && (
            <li style={{ gridArea: "selectlist" }} key="select">
              <label htmlFor="Select List" className="sr-only">
                {inputSelect.label}
              </label>
              {!inputSelectLabelAsOption && (
                <span>{inputSelect.label}</span>
              )}
              <div className={`select ${layoutVariant.selectWrapper} ${selectWrapperClassNames}`}>
                <select
                  name="Select List"
                  id="Select List"
                  defaultValue=""
                  className={`uppercase ${inputSelectClassNames}`}
                >
                  {inputSelectLabelAsOption && <option value="" disabled>
                    {inputSelect.label}
                  </option>}
                  {inputSelect.list.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className="focus"></span>
              </div>
            </li>
          )}
          {checkboxGroups && (
            <li style={{ gridArea: "checkboxes" }} key="cbg">
              {checkboxGroups.label && (
                <span
                  className={`italic text-accent mb-5 block ${layoutVariant.textCentering}`}
                >
                  {checkboxGroups.label}
                </span>
              )}
              <div className={`checkbox-groups flex flex-wrap gap-8 justify-start sm:justify-center ${checkboxGroupsWrapperClassNames}`}>
                {!checkboxGroups.nestedGroups && checkboxGroups.groups && checkboxGroups.groups.map((group, i) => (
                  <fieldset key={i} className="px-4 w-full sm:w-auto sm:px-0">
                    {group.legend && <legend>{group.legend}</legend>}
                    <ul className={`list-none grid gap-4 sm:grid-flow-col sm:grid-rows-3 md:grid-rows-2 ${checkboxListClassName}`}>
                      {group.list.map((label, j) => (
                        <li className="nowrap" key={j}>
                          <Checkbox
                            idName={`box_${i}_${j}`}
                            label={label}
                            inputClassName="bg-button-text border-button-bg"
                          />
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                ))}
                {checkboxGroups.nestedGroups && checkboxGroups.nestedGroups.map((group, i) => (
                  <fieldset key={i} className="px-4 w-full sm:w-auto sm:px-0">
                    {group.legend && <legend>{group.legend}</legend>}
                    <ul className={`list-none grid gap-4 sm:grid-flow-col sm:grid-rows-3 md:grid-rows-2 ${checkboxListClassName}`}>
                      {group.list.map((label, j) => (
                        <li className="nowrap" key={j}>
                          <Checkbox
                            idName={`box_${i}_${j}`}
                            label={label.value}
                            inputClassName="bg-button-text border-button-bg parent-checkbox"
                            inputProps={
                              {
                                onChange: (e) => checboxChangeHandler(e.target)
                              }
                            }
                          />
                          {label.sublist &&
                            <ul id={`list_box_${i}_${j}`} className="hidden list-none mt-4 grid gap-4 grid-cols-1 pl-6">
                              {label.sublist && label.sublist.map((item, k) => (
                                <li className="nowrap" key={k}>
                                  <Checkbox
                                    idName={`box_${i}_${j}_${k}`}
                                    label={item}
                                    inputClassName="bg-button-text border-button-bg"
                                  />
                                </li>
                              ))}
                            </ul>
                          }
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                ))}
              </div>
            </li>
          )}
          {inputTextAreaLabel && (
            <li
              style={{ gridArea: "textarea" }}
              className="text-center"
              key="ita"
            >
              <label htmlFor="textarea" className="sr-only">
                inputTextAreaLabel
              </label>
              <textarea
                id="textarea"
                placeholder={inputTextAreaLabel}
                required={inputTextAreaRequired}
                className={`border p-2 w-full max-w-[750px] min-w-[250px] min-h-[4rem] ${inputTextAreaClassName}`}
              ></textarea>
            </li>
          )}
          {inputRange && (
            <li style={{ gridArea: "range" }} key="range">
              <label htmlFor="Range" className="sr-only">
                {inputRange.label}
              </label>
              <div className="flex items-end">
                <span className="mr-1">{inputRange.label}</span>
                <input
                  type="text"
                  name="rangeMin"
                  id="rangeMin"
                  className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextRangeClassNames}`}
                />
                <span>To</span>
                <input
                  type="text"
                  name="rangeMax"
                  id="rangeMax"
                  className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextRangeClassNames}`}
                />
              </div>
            </li>
          )}
          {radiobtnGroup && (
            <li style={{ gridArea: "radiobuttons" }} key="rbg">
              {radiobtnGroup.label && (
                <span
                  className={`italic text-accent mb-5 block ${layoutVariant.textCentering} ${radiobtnGroupLabelClassNames}`}
                >
                  {radiobtnGroup.label}
                </span>
              )}
              <div className={`radiobox-groups flex flex-wrap gap-8 justify-start sm:justify-center ${radiobtnGroupWrapperClassNames}`}>
                <fieldset className="w-full sm:w-auto px-0">
                  <ul className={`list-none grid gap-4 sm:grid-flow-col sm:grid-rows-3 md:grid-rows-2 ${radiobtnListClassName}`}>
                    {radiobtnGroup.list?.[0] && radiobtnGroup.list.map((label, j) => (
                      <li className="nowrap" key={j}>
                        <RadioButton
                          id={`radio_${j}`}
                          name={`radioOption`}
                          label={label}
                          inputClassName="bg-button-text border-button-bg"
                          inputProps={{
                            value: label
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </fieldset>
              </div>
            </li>
          )}
          {addExtraTextInput && <li style={{ gridArea: "extrainput" }} key="extra">
            <label htmlFor="extraInput" className="sr-only">
              {extraTextInputPlaceholder}
            </label>
            <input
              type="text"
              name="extraInput"
              id="extraInput"
              placeholder={extraTextInputPlaceholder}
              className={`bg-transparent ${layoutVariant.inputNotCheckbox} ${inputTextClassNames}`}
            />
          </li>}
          {optInText && <li className={`col-span-full text-xs ${optInTextClassName}`}>
            <span dangerouslySetInnerHTML={{ __html: optInText }}></span>
          </li>}
          <li
            style={{ gridArea: "submit" }}
            className="text-center italic"
            key="submit"
          >
            <button
              disabled={isSendingRequest}
              type="submit"
              value="Submit"
              className={`max-w-sm border border-body-text px-3 capitalize cursor-pointer ${layoutVariant.inputNotCheckbox} ${submitButtonClassName}`}
            >
              {isSendingRequest ? (
                <>
                  <svg
                    role="status"
                    className="inline w-6 h-6 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-accent"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  Loading...
                </>
              ) : (
                "Submit"
              )}
            </button>
            {!hideReCaptchaText && <div className="description mx-auto max-w-sm md:max-w-md lg:max-w-sm">
              <span className={`mb-5 block ${layoutVariant.textCentering}`}>
                This site is protected by reCAPTCHA see our{" "}
                <a href="/privacy">Privacy Policy</a>
              </span>
            </div>}
          </li>
        </ul>
        {children && infoPosition == EInfoPosition.after && <> {children}</>}
      </fieldset>
    </form>
  );
};

export default ContactForm;
